import React, { useState, useEffect, useContext, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field, FieldArray } from "formik";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ChatiaDialog from "../ChatiaDialog";
import { Chip, FormControl, FormControlLabel, Grid, IconButton, InputLabel, MenuItem, Select, Switch, Typography, withStyles } from "@material-ui/core";
import CustomToggleSwitch from "../CustomToggleSwitch";
import BaseActionButton from "../ActionButtons";
import Autocomplete, { createFilterOptions } from "@material-ui/lab/Autocomplete";
import moment from "moment-timezone"
import { AuthContext } from "../../context/Auth/AuthContext";
import { isArray, capitalize } from "lodash";
import DeleteOutline from "@material-ui/icons/DeleteOutline";
import AttachFile from "@material-ui/icons/AttachFile";
import { head } from "lodash";
import ConfirmationModal from "../ConfirmationModal";
import MessageVariablesPicker from "../MessageVariablesPicker";
import useQueues from "../../hooks/useQueues";
import UserStatusIcon from "../UserModal/statusIcon";
import { Facebook, Instagram, WhatsApp } from "@material-ui/icons";
import { SelectArrowIcon } from "../ChatiaIcons";
import useTimezone from "../../hooks/useTimezone";
import DatePickerCustom from "../DatePickerCustom";
import useFilterDropdownStyles from "../SharedStyles/useFilterDropdownStyles";

const SCHEDULE_TIME_OPTIONS = (() => {
	const opts = [];
	for (let h = 0; h < 24; h++) {
		for (let m = 0; m < 60; m += 15) {
			opts.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
		}
	}
	return opts;
})();

const GreenSwitch = withStyles({
	root: {
		width: 36,
		height: 20,
		padding: 0,
		display: 'flex',
		overflow: 'visible',
	},
	switchBase: {
		padding: 2,
		color: '#fff !important',
		'&:hover': { backgroundColor: 'transparent !important' },
		'&$checked': {
			transform: 'translateX(16px)',
			color: '#fff !important',
			'&:hover': { backgroundColor: 'transparent !important' },
			'& + $track': {
				opacity: '1 !important',
				backgroundColor: '#4caf50 !important',
			},
		},
	},
	thumb: {
		width: 16,
		height: 16,
		boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
		backgroundColor: '#fff !important',
	},
	track: {
		borderRadius: 10,
		opacity: '1 !important',
		backgroundColor: '#9e9e9e !important',
	},
	checked: {},
})(Switch);

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	switchRow: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		padding: "10px 16px",
		borderRadius: 10,
		border: theme.palette.type === "dark" || theme.mode === "dark"
			? "1px solid rgba(255,255,255,0.10)"
			: "1px solid rgba(0,0,0,0.10)",
		background: theme.palette.type === "dark" || theme.mode === "dark"
			? "rgba(255,255,255,0.02)"
			: "transparent",
		marginTop: 8,
		marginBottom: 4,
		transition: "all 0.2s ease",
		"&:hover": {
			backgroundColor: theme.palette.type === "dark" || theme.mode === "dark"
				? "rgba(255,255,255,0.05)"
				: "#f5f5f5",
			borderColor: theme.palette.type === "dark" || theme.mode === "dark"
				? "rgba(255,255,255,0.20)"
				: "#bdbdbd",
		},
	},
	switchLabel: {
		fontSize: "0.9rem",
		fontWeight: 500,
		color: theme.palette.type === "dark" || theme.mode === "dark"
			? "#e9edef"
			: "#424242",
		userSelect: "none",
		cursor: "pointer",
	},
	// multFieldLine: {
	// 	display: "flex",
	// 	"& > *:not(:last-child)": {
	// 		marginRight: theme.spacing(1),
	// 	},
	// },

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
	// formControl: {
	// 	margin: theme.spacing(1),
	// 	minWidth: 120,
	// },
}));

const ScheduleSchema = Yup.object().shape({
	body: Yup.string()
		.min(5, i18n.t("scheduleModal.validations.tooShort"))
		.required(i18n.t("scheduleModal.validations.required")),
	contactId: Yup.number().required(i18n.t("scheduleModal.validations.required")),
	sendAt: Yup.string()
		.required(i18n.t("scheduleModal.validations.required"))
		.test(
			"not-past",
			i18n.t("scheduleModal.validations.notPast") || "A data não pode ser no passado.",
			(value) => {
				if (!value) return true;
				return moment(value).isSameOrAfter(moment().startOf("minute"));
			}
		)
});

const ScheduleModal = ({ open, onClose, scheduleId, contactId, cleanContact, reload }) => {
	const classes = useStyles();
	const dropdownClasses = useFilterDropdownStyles();
	const history = useHistory();
	const { user } = useContext(AuthContext);
	const isMounted = useRef(true);
	const { companyId } = user;

	const initialState = {
		body: "",
		contactId: "",
		sendAt: moment().add(1, 'hour').format('YYYY-MM-DDTHH:mm'),
		sentAt: "",
		openTicket: "enabled",
		ticketUserId: "",
		queueId: "",
		statusTicket: "closed",
		intervalo: 1,
		valorIntervalo: 0,
		enviarQuantasVezes: 1,
		tipoDias: 4,
		assinar: false
	};

	const initialContact = {
		id: "",
		name: "",
		channel: ""
	}

	const [schedule, setSchedule] = useState(initialState);
	const [currentContact, setCurrentContact] = useState(initialContact);
	const [contacts, setContacts] = useState([initialContact]);
	const [intervalo, setIntervalo] = useState(1);
	// const [valorIntervalo, setValorIntervalo] = useState(initialContact);
	// const [enviarQuantasVezes, setEnviarQuantasVezes] = useState(initialContact);
	const [tipoDias, setTipoDias] = useState(4);
	const [attachment, setAttachment] = useState(null);
	const attachmentFile = useRef(null);
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const messageInputRef = useRef();
	const [channelFilter, setChannelFilter] = useState("whatsapp");
	const [whatsapps, setWhatsapps] = useState([]);
	const [selectedWhatsapps, setSelectedWhatsapps] = useState([]);
	const [loading, setLoading] = useState(false);
	const [queues, setQueues] = useState([]);
	const [allQueues, setAllQueues] = useState([]);
	const [selectedUser, setSelectedUser] = useState(null);
	const [selectedQueue, setSelectedQueue] = useState(null);
	const { findAll: findAllQueues } = useQueues();
	const [options, setOptions] = useState([]);
	const [searchParam, setSearchParam] = useState("");

	// Hook para obter timezone da empresa
	const { getCompanyTimezone, getCurrentTimeInTimezone } = useTimezone();
	const [companyTimezone, setCompanyTimezone] = useState("America/Sao_Paulo");
	const [currentTimeInTz, setCurrentTimeInTz] = useState("");

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	// Carregar timezone da empresa e ajustar sendAt inicial
	useEffect(() => {
		let interval;
		const loadTimezone = async () => {
			try {
				const tzData = await getCompanyTimezone(companyId);
				const tz = tzData.timezone;
				setCompanyTimezone(tz);

				// Ajustar sendAt padrão para o timezone da empresa
				if (!scheduleId) {
					const nowInTz = moment().tz(tz).add(1, 'hour').format('YYYY-MM-DDTHH:mm');
					setSchedule(prev => ({ ...prev, sendAt: nowInTz }));
				}

				setCurrentTimeInTz(moment().tz(tz).format('DD/MM/YYYY HH:mm:ss'));

				interval = setInterval(() => {
					setCurrentTimeInTz(moment().tz(tz).format('DD/MM/YYYY HH:mm:ss'));
				}, 1000);
			} catch (error) {
				console.error("Erro ao carregar timezone:", error);
			}
		};

		if (open) {
			loadTimezone();
			return () => { if (interval) clearInterval(interval); };
		}
	}, [open, companyId, getCompanyTimezone, scheduleId]);

	useEffect(() => {
		if (isMounted.current) {
			const loadQueues = async () => {
				const list = await findAllQueues();
				setAllQueues(list);
				setQueues(list);
			};
			loadQueues();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (searchParam.length < 3) {
			setLoading(false);
			setSelectedQueue("");
			return;
		}
		const delayDebounceFn = setTimeout(() => {
			setLoading(true);
			const fetchUsers = async () => {
				try {
					const { data } = await api.get("/users/");
					setOptions(data.users);
					setLoading(false);
				} catch (err) {
					setLoading(false);
					toastError(err);
				}
			};

			fetchUsers();
		}, 500);
		return () => clearTimeout(delayDebounceFn);
	}, [searchParam]);

	useEffect(() => {
		api
			.get(`/whatsapp/filter`, { params: { session: 0, channel: channelFilter } })
			.then(({ data }) => {
				// Mapear os dados recebidos da API para adicionar a propriedade 'selected'
				const mappedWhatsapps = data.map((whatsapp) => ({
					...whatsapp,
					selected: false,
				}));

				setWhatsapps(mappedWhatsapps);
				if (mappedWhatsapps.length && mappedWhatsapps?.length === 1){
					setSelectedWhatsapps(mappedWhatsapps[0].id)
				}
			});
	}, [currentContact, channelFilter])

	useEffect(() => {
		if (contactId && contacts.length) {
			const contact = contacts.find(c => c.id === contactId);
			if (contact) {
				setCurrentContact(contact);
			}
		}
	}, [contactId, contacts]);

	useEffect(() => {
		const { companyId } = user;
		if (open) {
			try {
				(async () => {
					const { data: contactList } = await api.get('/contacts/list', { params: { companyId: companyId } });
					let customList = contactList.map((c) => ({ id: c.id, name: c.name, channel: c.channel }));
					if (isArray(customList)) {
						setContacts([{ id: "", name: "", channel: "" }, ...customList]);
					}
					if (contactId) {
						setSchedule(prevState => {
							return { ...prevState, contactId }
						});
					}

					if (!scheduleId) return;

					const { data } = await api.get(`/schedules/${scheduleId}`);
					
				// Converter sendAt de UTC para timezone da empresa
				const tzData = await getCompanyTimezone(companyId);
				const sendAtInTz = moment.utc(data.sendAt).tz(tzData.timezone).format('YYYY-MM-DDTHH:mm');

				setSchedule(prevState => {
					return { ...prevState, ...data, sendAt: sendAtInTz };
					});
					console.log(data)
					if (data.whatsapp) {
						setSelectedWhatsapps(data.whatsapp.id);
					}

					if (data.ticketUser) {
						setSelectedUser(data.ticketUser);
					}
					if (data.queueId) {
						setSelectedQueue(data.queueId);
					}

					if (data.intervalo) {
						setIntervalo(data.intervalo);
					}

					if (data.tipoDias) {
						setTipoDias(data.tipoDias);
					}

					setCurrentContact(data.contact);
				})()
			} catch (err) {
				toastError(err);
			}
		}
	}, [scheduleId, contactId, open, user]);

	const filterOptions = createFilterOptions({
		trim: true,
	});

	const handleClose = () => {
		onClose();
		setAttachment(null);
		setSchedule(initialState);
	};

	const handleAttachmentFile = (e) => {
		const file = head(e.target.files);
		if (file) {
			setAttachment(file);
		}
	};

	const IconChannel = (channel) => {
		switch (channel) {
			case "facebook":
				return <Facebook style={{ color: "#3b5998", verticalAlign: "middle" }} />;
			case "instagram":
				return <Instagram style={{ color: "#e1306c", verticalAlign: "middle" }} />;
			case "whatsapp":
				return <WhatsApp style={{ color: "#25d366", verticalAlign: "middle" }} />
			default:
				return "error";
		}
	};

	const renderOption = option => {
		if (option.name) {
			return <>
				{IconChannel(option.channel)}
				<Typography component="span" style={{ fontSize: 14, marginLeft: "10px", display: "inline-flex", alignItems: "center", lineHeight: "2" }}>
					{option.name}
				</Typography>
			</>
		} else {
			return `${i18n.t("newTicketModal.add")} ${option.name}`;
		}
	};
	const handleSaveSchedule = async values => {
		const scheduleData = {
			...values, userId: user.id, whatsappId: selectedWhatsapps, ticketUserId: selectedUser?.id || null,
			queueId: selectedQueue || null, intervalo: intervalo || 1, tipoDias: tipoDias || 4
		};

		try {
			if (scheduleId) {
				await api.put(`/schedules/${scheduleId}`, scheduleData);
				if (attachment != null) {
					const formData = new FormData();
					formData.append("file", attachment);
					await api.post(
						`/schedules/${scheduleId}/media-upload`,
						formData
					);
				}
			} else {
				const { data } = await api.post("/schedules", scheduleData);
				if (attachment != null) {
					const formData = new FormData();
					formData.append("file", attachment);
					await api.post(`/schedules/${data.id}/media-upload`, formData);
				}
			}
			toast.success(i18n.t("scheduleModal.success"));
			if (typeof reload == 'function') {
				reload();
			}
			if (contactId) {
				if (typeof cleanContact === 'function') {
					cleanContact();
					history.push('/schedules');
				}
			}
		} catch (err) {
			toastError(err);
		}
		setCurrentContact(initialContact);
		setSchedule(initialState);
		handleClose();
	};
	const handleClickMsgVar = async (msgVar, setValueFunc) => {
		const el = messageInputRef.current;
		const firstHalfText = el.value.substring(0, el.selectionStart);
		const secondHalfText = el.value.substring(el.selectionEnd);
		const newCursorPos = el.selectionStart + msgVar.length;

		setValueFunc("body", `${firstHalfText}${msgVar}${secondHalfText}`);

		await new Promise(r => setTimeout(r, 100));
		messageInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
	};

	const deleteMedia = async () => {
		if (attachment) {
			setAttachment(null);
			attachmentFile.current.value = null;
		}

		if (schedule.mediaPath) {
			await api.delete(`/schedules/${schedule.id}/media-upload`);
			setSchedule((prev) => ({
				...prev,
				mediaPath: null,
			}));
			toast.success(i18n.t("scheduleModal.toasts.deleted"));
			if (typeof reload == "function") {
				console.log(reload);
				console.log("1");
				reload();
			}
		}
	};

	const selectMenuProps = {
		classes: { paper: dropdownClasses.paper },
		anchorOrigin: { vertical: "bottom", horizontal: "left" },
		transformOrigin: { vertical: "top", horizontal: "left" },
		getContentAnchorEl: null,
		PaperProps: { style: { marginTop: 5 } },
	};

	return (
		<div className={classes.root}>
			<ConfirmationModal
				title={i18n.t("scheduleModal.confirmationModal.deleteTitle")}
				open={confirmationOpen}
				onClose={() => setConfirmationOpen(false)}
				onConfirm={deleteMedia}
			>
				{i18n.t("scheduleModal.confirmationModal.deleteMessage")}
			</ConfirmationModal>
			<Formik
				initialValues={schedule}
				enableReinitialize={true}
				validationSchema={ScheduleSchema}
				onSubmit={(values, actions) => {
					setTimeout(() => {
						handleSaveSchedule(values);
						actions.setSubmitting(false);
					}, 400);
				}}
			>
				{({ touched, errors, isSubmitting, values, setFieldValue, submitForm }) => {
					const canSaveSchedule = schedule.sentAt === null || schedule.sentAt === "";
					return (
						<ChatiaDialog
							open={open}
							onClose={handleClose}
							maxWidth="md"
							title={scheduleId ? i18n.t("scheduleModal.title.edit") || "Editar Agendamento" : i18n.t("scheduleModal.title.add") || "Agendar Mensagem"}
							hideFooter={!canSaveSchedule}
							onSave={submitForm}
							saveType="button"
							saveDisabled={isSubmitting}
							saveLoading={isSubmitting}
							saveText={scheduleId
								? `${i18n.t("scheduleModal.buttons.okEdit")}`
								: `${i18n.t("scheduleModal.buttons.okAdd")}`}
							footerChildren={
								!attachment && !schedule.mediaPath ? (
									<BaseActionButton
										onClick={() => attachmentFile.current.click()}
										disabled={isSubmitting}
										type="button"
										borderColor="rgb(139, 92, 246)"
										hoverBg="linear-gradient(to bottom, rgb(167, 139, 250), rgb(139, 92, 246))"
										iconSvg={
											<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
												<path d="M21 7H3V13C3 16.7712 3 18.6569 4.17157 19.8284C5.34315 21 7.22876 21 11 21H13C16.7712 21 18.6569 21 19.8284 19.8284C21 18.6569 21 16.7712 21 13V7Z" />
												<path d="M21 7H3L4.2 5.4C5.08328 4.22229 5.52492 3.63344 6.15836 3.31672C6.7918 3 7.52786 3 9 3H15C16.4721 3 17.2082 3 17.8416 3.31672C18.4751 3.63344 18.9167 4.22229 19.8 5.4L21 7Z" />
												<path d="M12 11L12 17.5M9 13.5C9.58984 12.8932 11.1597 10.5 12 10.5C12.8403 10.5 14.4102 12.8932 15 13.5" />
											</svg>
										}
									>
										{i18n.t("quickMessages.buttons.attach")}
									</BaseActionButton>
								) : null
							}
						>
							<div style={{ display: "none" }}>
								<input
									type="file"
									accept=".png,.jpg,.jpeg"
									ref={attachmentFile}
									onChange={(e) => handleAttachmentFile(e)}
								/>
							</div>
							<Form>
								<div className={classes.multFieldLine}>
									<FormControl
										variant="outlined"
										fullWidth
									>
										<Autocomplete
											fullWidth
											size="small"
											popupIcon={<SelectArrowIcon />}
											classes={{ paper: dropdownClasses.paper }}
											value={currentContact}
											options={contacts}
											onChange={(e, contact) => {
												const contactId = contact ? contact.id : '';
												setSchedule({ ...schedule, contactId });
												setCurrentContact(contact ? contact : initialContact);
												setChannelFilter(contact ? contact.channel : "whatsapp");
											}}
											getOptionLabel={(option) => option.name}
											renderOption={renderOption}
											getOptionSelected={(option, value) => {
												return value.id === option.id
											}}
											renderInput={(params) => <TextField {...params} variant="outlined" placeholder={i18n.t("scheduleModal.form.contact")} />}
										/>
									</FormControl>
								</div>
								<div className={classes.multFieldLine}>
									<Field
										as={TextField}
										minRows={9}
										multiline={true}
										label={i18n.t("scheduleModal.form.body")}
										name="body"
										inputRef={messageInputRef}
										error={touched.body && Boolean(errors.body)}
										helperText={touched.body && errors.body}
										variant="outlined"
										margin="dense"
										fullWidth
									/>
								</div>
								<Grid item xs={12} md={12} xl={6}>
									<MessageVariablesPicker
										disabled={isSubmitting}
										onClick={value => handleClickMsgVar(value, setFieldValue)}
									/>
								</Grid>
								<Grid container spacing={2} style={{ marginTop: 4 }}>
									{/* Conexão | Abrir ticket */}
									<Grid item xs={12} md={6}>
										<FormControl variant="outlined" fullWidth size="small">
											<InputLabel id="whatsapp-selection-label">
												{i18n.t("campaigns.dialog.form.whatsapp")}
											</InputLabel>
											<Field
												as={Select}
												label={i18n.t("campaigns.dialog.form.whatsapp")}
												labelId="whatsapp-selection-label"
												id="whatsappIds"
												name="whatsappIds"
												required
												error={touched.whatsappId && Boolean(errors.whatsappId)}
												value={selectedWhatsapps}
												onChange={(event) => setSelectedWhatsapps(event.target.value)}
												IconComponent={SelectArrowIcon}
												MenuProps={selectMenuProps}
											>
												{whatsapps &&
													whatsapps.map((whatsapp) => (
														<MenuItem key={whatsapp.id} value={whatsapp.id}>
															{whatsapp.name}
														</MenuItem>
													))}
											</Field>
										</FormControl>
									</Grid>
									<Grid item xs={12} md={6}>
										<FormControl variant="outlined" fullWidth size="small">
											<InputLabel id="openTicket-selection-label">
												{i18n.t("campaigns.dialog.form.openTicket")}
											</InputLabel>
											<Field
												as={Select}
												label={i18n.t("campaigns.dialog.form.openTicket")}
												labelId="openTicket-selection-label"
												id="openTicket"
												name="openTicket"
												error={touched.openTicket && Boolean(errors.openTicket)}
												IconComponent={SelectArrowIcon}
												MenuProps={selectMenuProps}
											>
												<MenuItem value={"enabled"}>{i18n.t("campaigns.dialog.form.enabledOpenTicket")}</MenuItem>
												<MenuItem value={"disabled"}>{i18n.t("campaigns.dialog.form.disabledOpenTicket")}</MenuItem>
											</Field>
										</FormControl>
									</Grid>

									{/* Usuário | Fila */}
									<Grid item xs={12} md={6}>
										<Autocomplete
											variant="outlined"
											getOptionLabel={(option) => `${option.name}`}
											value={selectedUser}
											size="small"
											popupIcon={<SelectArrowIcon />}
											classes={{ paper: dropdownClasses.paper }}
											onChange={(e, newValue) => {
												setSelectedUser(newValue);
												if (newValue != null && Array.isArray(newValue.queues)) {
													if (newValue.queues.length === 1) {
														setSelectedQueue(newValue.queues[0].id);
													}
													setQueues(newValue.queues);
												} else {
													setQueues(allQueues);
													setSelectedQueue("");
												}
											}}
											options={options}
											filterOptions={filterOptions}
											freeSolo
											fullWidth
											disabled={values.openTicket === "disabled"}
											autoHighlight
											noOptionsText={i18n.t("transferTicketModal.noOptions")}
											loading={loading}
											renderOption={option => (<span> <UserStatusIcon user={option} /> {option.name}</span>)}
											renderInput={(params) => (
												<TextField
													{...params}
													label={i18n.t("transferTicketModal.fieldLabel")}
													variant="outlined"
													onChange={(e) => setSearchParam(e.target.value)}
													InputProps={{
														...params.InputProps,
														endAdornment: (
															<React.Fragment>
																{loading ? (
																	<CircularProgress color="inherit" size={20} />
																) : null}
																{params.InputProps.endAdornment}
															</React.Fragment>
														),
													}}
												/>
											)}
										/>
									</Grid>
									<Grid item xs={12} md={6}>
										<FormControl variant="outlined" fullWidth size="small">
											<InputLabel>
												{i18n.t("transferTicketModal.fieldQueueLabel")}
											</InputLabel>
											<Select
												value={selectedQueue}
												onChange={(e) => setSelectedQueue(e.target.value)}
												label={i18n.t("transferTicketModal.fieldQueuePlaceholder")}
												disabled={values.openTicket === "disabled"}
												IconComponent={SelectArrowIcon}
												MenuProps={selectMenuProps}
											>
												{queues.map((queue) => (
													<MenuItem key={queue.id} value={queue.id}>
														{queue.name}
													</MenuItem>
												))}
											</Select>
										</FormControl>
									</Grid>

									{/* Status ticket | Data */}
									<Grid item xs={12} md={6}>
										<FormControl variant="outlined" fullWidth size="small">
											<InputLabel id="statusTicket-selection-label">
												{i18n.t("campaigns.dialog.form.statusTicket")}
											</InputLabel>
											<Field
												as={Select}
												disabled={values.openTicket === "disabled"}
												label={i18n.t("campaigns.dialog.form.statusTicket")}
												labelId="statusTicket-selection-label"
												id="statusTicket"
												name="statusTicket"
												error={touched.statusTicket && Boolean(errors.statusTicket)}
												IconComponent={SelectArrowIcon}
												MenuProps={selectMenuProps}
											>
												<MenuItem value={"closed"}>{i18n.t("campaigns.dialog.form.closedTicketStatus")}</MenuItem>
												<MenuItem value={"open"}>{i18n.t("campaigns.dialog.form.openTicketStatus")}</MenuItem>
											</Field>
										</FormControl>
									</Grid>
										<Grid item xs={12} md={3}>
											<DatePickerCustom
												label={i18n.t("scheduleModal.form.date") || "Data"}
												value={values.sendAt ? values.sendAt.split("T")[0] : ""}
												onChange={(dateStr) => {
													const time = (values.sendAt && values.sendAt.includes("T")) ? values.sendAt.split("T")[1] : "09:00";
													setFieldValue("sendAt", dateStr ? `${dateStr}T${time}` : "");
												}}
											/>
										</Grid>
										<Grid item xs={12} md={3}>
											<Autocomplete
												freeSolo size="small"
												classes={{ paper: dropdownClasses.paper }}
												options={SCHEDULE_TIME_OPTIONS}
												inputValue={(values.sendAt && values.sendAt.includes("T")) ? values.sendAt.split("T")[1] : "09:00"}
												onInputChange={(e, v, reason) => {
													if (reason === "reset") return;
													const digits = v.replace(/[^0-9]/g, "");
													let next = v;
													if (digits.length >= 4 && !v.includes(":")) {
														const hh = Math.min(parseInt(digits.slice(0, 2), 10), 23);
														const mm = Math.min(parseInt(digits.slice(2, 4), 10), 59);
														next = `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
													}
													const date = (values.sendAt && values.sendAt.includes("T")) ? values.sendAt.split("T")[0] : moment().format("YYYY-MM-DD");
													setFieldValue("sendAt", `${date}T${next}`);
												}}
												onChange={(e, v) => {
													if (!v) return;
													const date = (values.sendAt && values.sendAt.includes("T")) ? values.sendAt.split("T")[0] : moment().format("YYYY-MM-DD");
													setFieldValue("sendAt", `${date}T${v}`);
												}}
												disableClearable
												getOptionLabel={(o) => o || ""}
												renderInput={(params) => (
													<TextField {...params} variant="outlined" margin="dense"
														label={i18n.t("scheduleModal.form.time") || "Hora"}
														placeholder="09:00"
														InputLabelProps={{ shrink: true }}
														error={touched.sendAt && Boolean(errors.sendAt)}
														helperText={
															touched.sendAt && errors.sendAt
																? errors.sendAt
																: currentTimeInTz
																	? i18n.t("scheduleModal.form.timezoneHelper", { tz: companyTimezone, time: currentTimeInTz })
																	: i18n.t("scheduleModal.form.timezoneHelperShort", { tz: companyTimezone })
														}
													/>
												)}
												ListboxProps={{ style: { maxHeight: 200 } }}
											/>
										</Grid>

									{/* Assinar */}
									<Grid item xs={12}>
										<div className={classes.switchRow}>
											<span
												className={classes.switchLabel}
												onClick={() => values.openTicket !== "disabled" && setFieldValue("assinar", !values.assinar)}
											>
												{i18n.t("scheduleModal.form.assinar")}
											</span>
											<CustomToggleSwitch
												checked={values.assinar}
												onChange={() => setFieldValue("assinar", !values.assinar)}
												disabled={values.openTicket === "disabled"}
											/>
										</div>
									</Grid>
								</Grid>
								<h3>{i18n.t("scheduleModal.recurrence.title")}</h3>
								<p>
									{i18n.t("scheduleModal.recurrence.description")}
								</p>
								<br />
								<Grid container spacing={1}>
									<Grid item xs={12} md={4} xl={4}>
										<FormControl size="small" fullWidth variant="outlined">
											<InputLabel id="demo-simple-select-label">{i18n.t("scheduleModal.recurrence.interval")}</InputLabel>
											<Select
												labelId="demo-simple-select-label"
												id="demo-simple-select"
												value={intervalo}
												onChange={(e) =>
													setIntervalo(e.target.value || 1)
												}
												label={i18n.t("scheduleModal.recurrence.interval")}
												IconComponent={SelectArrowIcon}
												MenuProps={selectMenuProps}
											>
												<MenuItem value={1}>{i18n.t("scheduleModal.recurrence.intervalTypes.days")}</MenuItem>
												<MenuItem value={2}>{i18n.t("scheduleModal.recurrence.intervalTypes.weeks")}</MenuItem>
												<MenuItem value={3}>{i18n.t("scheduleModal.recurrence.intervalTypes.months")}</MenuItem>
												<MenuItem value={4}>{i18n.t("scheduleModal.recurrence.intervalTypes.minutes")}</MenuItem>
											</Select>
										</FormControl>
									</Grid>

									<Grid item xs={12} md={4} xl={4}>
										<Field
											as={TextField}
											label={i18n.t("scheduleModal.recurrence.intervalValue")}
											name="valorIntervalo"
											size="small"
											error={touched.valorIntervalo && Boolean(errors.valorIntervalo)}
											InputLabelProps={{ shrink: true }}
											variant="outlined"
											fullWidth
										/>
									</Grid>
									<Grid item xs={12} md={4} xl={4}>
										<Field
											as={TextField}
											label={i18n.t("scheduleModal.recurrence.sendTimes")}
											name="enviarQuantasVezes"
											size="small"
											error={
												touched.enviarQuantasVezes &&
												Boolean(errors.enviarQuantasVezes)
											}
											variant="outlined"
											fullWidth
										/>
									</Grid>
									<Grid item xs={12} md={12} xl={12}>
										<FormControl size="small" fullWidth variant="outlined">
											<InputLabel id="demo-simple-select-label">{i18n.t("scheduleModal.recurrence.sendTimes")}</InputLabel>
											<Select
												labelId="demo-simple-select-label"
												id="demo-simple-select"
												value={tipoDias}
												onChange={(e) =>
													setTipoDias(e.target.value || 4)
												}
												label={i18n.t("scheduleModal.recurrence.sendTimes")}
												IconComponent={SelectArrowIcon}
												MenuProps={selectMenuProps}
											>
												<MenuItem value={4}>{i18n.t("scheduleModal.recurrence.businessDays.normal")}</MenuItem>
												<MenuItem value={5}>{i18n.t("scheduleModal.recurrence.businessDays.before")}</MenuItem>
												<MenuItem value={6}>{i18n.t("scheduleModal.recurrence.businessDays.after")}</MenuItem>
											</Select>
										</FormControl>
									</Grid>
								</Grid>
								{(schedule.mediaPath || attachment) && (
									<Grid xs={12} item>
										<Button startIcon={<AttachFile />}>
											{attachment ? attachment.name : schedule.mediaName}
										</Button>
										<IconButton
											onClick={() => setConfirmationOpen(true)}
											color="secondary"
										>
											<DeleteOutline color="secondary" />
										</IconButton>
									</Grid>
								)}
							</Form>
						</ChatiaDialog>
					);
				}}
			</Formik>
		</div>
	);
};

export default ScheduleModal;
